import { Button, Modal, type UploadFile } from "antd"
import type { Dayjs } from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { CustomDatePicker } from "../../../components/Date&TimePickers/CustomDatePicker"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { uploadSubcontractorComplianceDocument } from "../redux/subcontractors.thunks.ts"
import { COMPLIANCE_DOCUMENT_LABELS } from "../constants/compliance.ts"
import { DocumentUpload } from "./SubcontractorForm/DocumentUpload.tsx"
import type {
  SubcontractorComplianceDocument,
  SubcontractorComplianceDocumentType
} from "../types/types.ts"

interface ComplianceDocumentUploadModalProps {
  open: boolean
  subcontractorId: string
  documentType: SubcontractorComplianceDocumentType
  onClose: () => void
  onUploaded: (document: SubcontractorComplianceDocument) => void
}

/**
 * Upload a missing compliance document, or replace one that already exists —
 * one modal for both, since the backend endpoint itself makes no distinction
 * (`upload_compliance_document` replaces in place, keyed on `document_type`).
 * The only caller-visible difference is which label opened it.
 */
export const ComplianceDocumentUploadModal = ({
  open,
  subcontractorId,
  documentType,
  onClose,
  onUploaded
}: ComplianceDocumentUploadModalProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [expirationDate, setExpirationDate] = useState<Dayjs | null>(null)
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setFileList([])
    setExpirationDate(null)
    setSaving(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSave = async () => {
    const file = fileList[0]?.originFileObj as File | undefined
    if (!file) return

    setSaving(true)
    const result = await dispatch(
      uploadSubcontractorComplianceDocument({
        id: subcontractorId,
        document_type: documentType,
        file,
        expiration_date: expirationDate ? expirationDate.format("YYYY-MM-DD") : undefined
      })
    )
    setSaving(false)

    if (uploadSubcontractorComplianceDocument.fulfilled.match(result)) {
      onUploaded(result.payload as SubcontractorComplianceDocument)
      reset()
    }
  }

  return (
    <Modal
      open={open}
      title={t("subcontractorDetail.compliance.uploadTitle", {
        document: COMPLIANCE_DOCUMENT_LABELS[documentType] || documentType
      })}
      onCancel={handleClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t("subcontractorDetail.compliance.cancel")}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!fileList.length}
          onClick={handleSave}
        >
          {t("subcontractorDetail.compliance.save")}
        </Button>
      ]}
    >
      <div className="flex flex-col gap-3 pt-2">
        <DocumentUpload
          buttonLabel={t("subcontractorDetail.compliance.selectFile")}
          fileList={fileList}
          onChange={({ fileList: next }) => setFileList(next)}
          onRemove={() => setFileList([])}
        />
        <CustomDatePicker
          className={"w-full"}
          size="large"
          format="YYYY-MM-DD"
          placeholder={t("subcontractorDetail.compliance.expirationDatePlaceholder")}
          value={expirationDate}
          onChange={setExpirationDate}
        />
      </div>
    </Modal>
  )
}
